// Vorrat – Bestand, Haltbarkeit, Lagerort.
//
// Neu: Filter (unter Meldebestand / läuft ab / nie bestätigt), Produktbilder
// (die waren immer gespeichert und wurden nie gezeigt), Chargen mit MHD und
// die „Bald verbrauchen"-Ansicht als eigener Reiter.
import {
  STORAGE_LOCATIONS,
  findCategory,
  formatEuro,
  fromBaseSize,
  toBaseSize,
} from '@speis/shared';
import type { BatchView, ProductView } from '@speis/shared';
import { api } from '../core/api.js';
import { guard, guarded } from '../core/guard.js';
import { refreshExpiring } from '../core/sse.js';
import { applyProducts, removeProduct, state, subscribe, upsertProduct } from '../core/store.js';
import { clear, el, haptic, replace } from '../ui/dom.js';
import { confirmDialog } from '../ui/dialog.js';
import { icon } from '../ui/icons.js';
import { openSheet } from '../ui/sheet.js';
import { toast } from '../ui/toast.js';
import { describeStock, expiryBadge, freshnessDot, productThumb, sectionTitle } from './shared.js';

type Filter = 'all' | 'expiring' | 'low' | 'unconfirmed';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'Alles' },
  { key: 'expiring', label: 'Läuft ab' },
  { key: 'low', label: 'Meldebestand' },
  { key: 'unconfirmed', label: 'Unbestätigt' },
];

let filter: Filter = 'all';
let query = '';
let unsubscribe: (() => void) | null = null;
let host: HTMLElement | null = null;

export function renderPantry(container: HTMLElement, initialFilter?: string): void {
  host = container;
  unsubscribe?.();
  if (initialFilter && FILTERS.some((f) => f.key === initialFilter))
    filter = initialFilter as Filter;

  clear(container);
  container.append(buildToolbar(), el('div', { class: 'pantry-body' }));
  draw();

  unsubscribe = subscribe((changed) => {
    if (changed.has('products') || changed.has('expiring')) draw();
  });
}

export function teardownPantry(): void {
  unsubscribe?.();
  unsubscribe = null;
  host = null;
}

function buildToolbar(): HTMLElement {
  const search = el('input', {
    class: 'input',
    type: 'search',
    placeholder: 'Vorrat durchsuchen …',
    autocomplete: 'off',
    value: query,
    'aria-label': 'Vorrat durchsuchen',
  });
  search.addEventListener('input', () => {
    query = search.value;
    draw();
  });

  const chips = el('div', { class: 'chip-row filter-row', role: 'group', 'aria-label': 'Filter' });
  for (const f of FILTERS) {
    chips.append(
      el(
        'button',
        {
          class: 'chip',
          type: 'button',
          'aria-pressed': String(filter === f.key),
          onclick: () => {
            filter = f.key;
            draw();
            for (const chip of chips.children) {
              chip.setAttribute(
                'aria-pressed',
                String(chip.textContent?.startsWith(f.label) ?? false),
              );
            }
          },
        },
        [f.label, countFor(f.key) ? el('span', { class: 'tiny' }, String(countFor(f.key))) : null],
      ),
    );
  }

  return el('div', { class: 'pantry-toolbar' }, [
    el('div', { class: 'search' }, [icon('search', { size: 18 }), search]),
    chips,
  ]);
}

function countFor(key: Filter): number {
  if (key === 'expiring') return state.expiring.length;
  if (key === 'low') return state.products.filter((p) => p.belowMin).length;
  if (key === 'unconfirmed') return state.products.filter((p) => !p.confirmed).length;
  return 0;
}

function draw(): void {
  const body = host?.querySelector<HTMLElement>('.pantry-body');
  if (!body) return;
  clear(body);

  if (filter === 'expiring') {
    body.append(renderExpiring());
    return;
  }

  const q = query.trim().toLowerCase();
  let products = state.products;
  if (q) {
    products = products.filter((p) =>
      `${p.name} ${p.brand ?? ''} ${findCategory(p.category).label}`.toLowerCase().includes(q),
    );
  }
  if (filter === 'low') products = products.filter((p) => p.belowMin);
  if (filter === 'unconfirmed') products = products.filter((p) => !p.confirmed);

  if (!products.length) {
    body.append(emptyState(q));
    return;
  }

  const groups = new Map<string, ProductView[]>();
  for (const p of products) {
    const key = findCategory(p.category).key;
    const list = groups.get(key) ?? [];
    list.push(p);
    groups.set(key, list);
  }

  const ordered = [...groups.entries()].sort((a, b) =>
    findCategory(a[0]).label.localeCompare(findCategory(b[0]).label, 'de'),
  );

  for (const [categoryKey, items] of ordered) {
    const category = findCategory(categoryKey);
    body.append(
      el('h2', { class: 'cat-head' }, [
        el('span', { class: 'cat-icon', 'aria-hidden': 'true' }, category.icon),
        el('span', {}, category.label),
      ]),
    );
    const list = el('ul', { class: 'list' });
    for (const p of items.sort((a, b) => a.name.localeCompare(b.name, 'de'))) {
      list.append(productRow(p));
    }
    body.append(list);
  }
}

function productRow(p: ProductView): HTMLElement {
  const category = findCategory(p.category);

  const meta = el('div', { class: 'item-meta' }, [
    freshnessDot(p.freshness),
    el('span', {}, describeStock(p)),
  ]);
  const expiry = expiryBadge(p.daysUntilExpiry);
  if (expiry) meta.append(expiry);
  if (p.belowMin) meta.append(el('span', { class: 'badge badge-warn' }, 'Meldebestand'));
  if (!p.confirmed) meta.append(el('span', { class: 'badge' }, 'provisorisch'));

  const body = el(
    'button',
    {
      class: 'item-body item-open',
      type: 'button',
      onclick: () => openProductSheet(p),
    },
    [el('div', { class: 'item-name' }, p.brand ? `${p.name} · ${p.brand}` : p.name), meta],
  );

  const stepper = el('div', { class: 'stepper' }, [
    el(
      'button',
      {
        type: 'button',
        'aria-label': `Ein ${p.name} weniger`,
        disabled: p.closedCount <= 0,
        onclick: () => void adjust(p, -1),
      },
      '−',
    ),
    el('span', { class: 'value', 'aria-live': 'polite' }, String(p.closedCount)),
    el(
      'button',
      { type: 'button', 'aria-label': `Ein ${p.name} mehr`, onclick: () => void adjust(p, +1) },
      '+',
    ),
  ]);

  return el('li', { class: `list-item${p.belowMin ? ' is-low' : ''}` }, [
    productThumb(p, category.icon),
    body,
    stepper,
  ]);
}

function emptyState(q: string): HTMLElement {
  if (q) {
    return el('div', { class: 'empty' }, [
      el('div', { class: 'empty-icon', 'aria-hidden': 'true' }, '🔍'),
      el('p', { class: 'empty-title' }, 'Kein Treffer'),
      el('p', { class: 'empty-text' }, `Zu „${q}" ist nichts im Vorrat.`),
    ]);
  }
  if (filter !== 'all') {
    return el('div', { class: 'empty' }, [
      el('div', { class: 'empty-icon', 'aria-hidden': 'true' }, '👍'),
      el('p', { class: 'empty-title' }, 'Nichts zu tun'),
      el('p', { class: 'empty-text' }, 'In diesem Filter ist gerade alles in Ordnung.'),
    ]);
  }
  return el('div', { class: 'empty' }, [
    el('div', { class: 'empty-icon', 'aria-hidden': 'true' }, '🧺'),
    el('p', { class: 'empty-title' }, 'Noch kein Vorrat erfasst'),
    el(
      'p',
      { class: 'empty-text' },
      'Scanne einen Barcode oder einen Kassenbon über den ＋-Knopf – daraus baut sich der Katalog von selbst.',
    ),
  ]);
}

// ── „Bald verbrauchen" ────────────────────────────────────────────────

function renderExpiring(): HTMLElement {
  const wrap = el('div', {});

  if (!state.expiring.length) {
    wrap.append(
      el('div', { class: 'empty' }, [
        el('div', { class: 'empty-icon', 'aria-hidden': 'true' }, '🌿'),
        el('p', { class: 'empty-title' }, 'Nichts läuft ab'),
        el(
          'p',
          { class: 'empty-text' },
          'In den nächsten sieben Tagen wird nichts fällig. Beim Einkaufen erfasste Haltbarkeitsdaten landen hier automatisch.',
        ),
      ]),
    );
    return wrap;
  }

  const expired = state.expiring.filter((i) => i.daysLeft < 0);
  const soon = state.expiring.filter((i) => i.daysLeft >= 0);

  if (expired.length) {
    wrap.append(sectionTitle('Abgelaufen'));
    const list = el('ul', { class: 'list' });
    for (const item of expired) list.append(expiringRow(item, true));
    wrap.append(list);
  }
  if (soon.length) {
    wrap.append(sectionTitle('In den nächsten Tagen'));
    const list = el('ul', { class: 'list' });
    for (const item of soon) list.append(expiringRow(item, false));
    wrap.append(list);
  }

  wrap.append(
    el(
      'p',
      { class: 'tiny expiry-hint' },
      'Tipp: „Was kann ich kochen?" im Rezepte-Tab schlägt bevorzugt Gerichte vor, die genau diese Sachen verbrauchen.',
    ),
  );
  return wrap;
}

function expiringRow(item: (typeof state.expiring)[number], isExpired: boolean): HTMLElement {
  const product = state.products.find((p) => p.id === item.productId);
  const category = findCategory(product?.category);

  return el('li', { class: `list-item${isExpired ? ' is-expired' : ''}` }, [
    product
      ? productThumb(product, category.icon)
      : el('span', { class: 'item-thumb thumb-fallback' }, category.icon),
    el('div', { class: 'item-body' }, [
      el('div', { class: 'item-name' }, `${item.qty}× ${item.name}`),
      el('div', { class: 'item-meta' }, [
        expiryBadge(item.daysLeft),
        item.pricePaid != null
          ? el('span', { class: 'tabular' }, formatEuro(item.pricePaid))
          : null,
      ]),
    ]),
    el('div', { class: 'row-actions' }, [
      el(
        'button',
        {
          class: 'btn btn-sm btn-secondary',
          type: 'button',
          onclick: guarded(() => resolveBatch(item.batchId, 'consumed'), {
            success: 'Aufgebraucht – schön!',
          }),
        },
        'Gegessen',
      ),
      el(
        'button',
        {
          class: 'btn btn-sm btn-danger',
          type: 'button',
          onclick: guarded(() => resolveBatch(item.batchId, 'wasted'), {
            success: 'Notiert – landet in der Statistik',
          }),
        },
        'Weg',
      ),
    ]),
  ]);
}

async function resolveBatch(batchId: number, outcome: 'consumed' | 'wasted'): Promise<void> {
  await api(`/api/batches/${batchId}/resolve`, { method: 'POST', body: { outcome } });
  await Promise.all([refreshProducts(), refreshExpiring()]);
}

// ── Bestand ändern ────────────────────────────────────────────────────

async function adjust(p: ProductView, delta: number): Promise<void> {
  haptic(8);
  // Beim Zubuchen gleich nach dem Haltbarkeitsdatum fragen – das ist der
  // einzige Moment, in dem man die Packung tatsächlich in der Hand hat.
  if (delta > 0) {
    openAddStockSheet(p, 1);
    return;
  }
  await guard(
    async () => {
      const { product } = await api<{ product: ProductView }>(`/api/products/${p.id}/adjust`, {
        method: 'POST',
        body: { deltaClosed: delta, source: 'correction' },
      });
      upsertProduct(product);
    },
    { failure: 'Buchen fehlgeschlagen' },
  );
}

export function openAddStockSheet(p: ProductView, initialQty = 1): void {
  let qty = initialQty;
  let suggested: string | null = null;

  const qtyValue = el('span', { class: 'value' }, String(qty));
  const stepper = el('div', { class: 'stepper' }, [
    el('button', { type: 'button', 'aria-label': 'Weniger', onclick: () => setQty(qty - 1) }, '−'),
    qtyValue,
    el('button', { type: 'button', 'aria-label': 'Mehr', onclick: () => setQty(qty + 1) }, '+'),
  ]);
  function setQty(next: number): void {
    qty = Math.max(1, Math.min(99, next));
    qtyValue.textContent = String(qty);
  }

  const date = el('input', {
    class: 'input',
    type: 'date',
    'aria-label': 'Mindestens haltbar bis',
  });
  const price = el('input', {
    class: 'input',
    type: 'text',
    inputmode: 'decimal',
    placeholder: 'Preis pro Packung (optional)',
  });

  const quickRow = el('div', { class: 'chip-row' });
  for (const [label, days] of [
    ['3 Tage', 3],
    ['1 Woche', 7],
    ['2 Wochen', 14],
    ['1 Monat', 30],
    ['1 Jahr', 365],
  ] as const) {
    quickRow.append(
      el(
        'button',
        {
          class: 'chip',
          type: 'button',
          onclick: () => {
            date.value = new Date(Date.now() + days * 86_400_000).toLocaleDateString('sv-SE');
          },
        },
        label,
      ),
    );
  }

  const body = el('div', {}, [
    el('p', { class: 'muted' }, p.brand ? `${p.name} · ${p.brand}` : p.name),
    el('div', { class: 'field' }, [el('label', {}, 'Anzahl Packungen'), stepper]),
    el('div', { class: 'field' }, [
      el('label', {}, 'Mindestens haltbar bis'),
      date,
      quickRow,
      el(
        'p',
        { class: 'field-hint' },
        'Leer lassen ist in Ordnung – dann taucht es nur nicht in „Läuft ab" auf.',
      ),
    ]),
    el('div', { class: 'field' }, [el('label', {}, 'Preis'), price]),
  ]);

  const sheet = openSheet(body, { title: 'Bestand zubuchen' });

  // Vorschlag aus der gelernten Haltbarkeit des Produkts.
  void api<{ suggestedBestBefore: string | null }>(`/api/products/${p.id}/batches`).then((r) => {
    suggested = r.suggestedBestBefore;
    if (suggested && !date.value) date.value = suggested;
  });

  sheet.addActions(
    el(
      'button',
      {
        class: 'btn btn-primary btn-block',
        type: 'button',
        onclick: guarded(
          async () => {
            const { product } = await api<{ product: ProductView }>(
              `/api/products/${p.id}/adjust`,
              {
                method: 'POST',
                body: {
                  deltaClosed: qty,
                  bestBefore: date.value || null,
                  pricePaid: price.value ? Number(price.value.replace(',', '.')) : null,
                  source: 'barcode_scan',
                },
              },
            );
            upsertProduct(product);
            void refreshExpiring();
            sheet.close();
          },
          { success: 'Gebucht', failure: 'Buchen fehlgeschlagen' },
        ),
      },
      'Buchen',
    ),
  );
}

// ── Produkt bearbeiten ────────────────────────────────────────────────

export function openProductSheet(p: ProductView): void {
  const name = el('input', { class: 'input', type: 'text', value: p.name });
  const brand = el('input', {
    class: 'input',
    type: 'text',
    value: p.brand ?? '',
    placeholder: 'Marke',
  });

  const category = el('select', { class: 'select' });
  for (const c of [{ key: '', label: '— Kategorie —', icon: '' }, ...CATEGORY_OPTIONS]) {
    const option = el('option', { value: c.key }, c.icon ? `${c.icon} ${c.label}` : c.label);
    if (findCategory(p.category).key === c.key) option.selected = true;
    category.append(option);
  }

  const location = el('select', { class: 'select' });
  for (const l of [{ key: '', label: '— Lagerort —', icon: '' }, ...STORAGE_LOCATIONS]) {
    const option = el('option', { value: l.key }, l.icon ? `${l.icon} ${l.label}` : l.label);
    if ((p.storageLocation ?? '') === l.key) option.selected = true;
    location.append(option);
  }

  const size = fromBaseSize(p.packageSizeValue, p.packageSizeUnit);
  const sizeValue = el('input', {
    class: 'input',
    type: 'text',
    inputmode: 'decimal',
    value: size.value === '' ? '' : String(size.value),
    placeholder: 'z.B. 1000',
  });
  const sizeUnit = el('select', { class: 'select' });
  for (const u of ['', 'Stück', 'ml', 'l', 'g', 'kg']) {
    const option = el('option', { value: u }, u || '—');
    if (u === size.unit) option.selected = true;
    sizeUnit.append(option);
  }

  const minStock = el('input', {
    class: 'input',
    type: 'number',
    inputmode: 'numeric',
    min: 0,
    value: p.minStock ?? '',
    placeholder: 'z.B. 2',
  });

  const coreSwitch = el('button', {
    class: 'switch',
    type: 'button',
    role: 'switch',
    'aria-checked': String(p.isCore),
    'aria-label': 'Kernartikel',
  });
  coreSwitch.addEventListener('click', () => {
    const next = coreSwitch.getAttribute('aria-checked') !== 'true';
    coreSwitch.setAttribute('aria-checked', String(next));
  });

  const batchWrap = el('div', { class: 'batch-list' });
  const aliasWrap = el('div', { class: 'chip-row' });

  const body = el('div', {}, [
    el('div', { class: 'prod-status' }, [
      el(
        'span',
        { class: `badge ${p.confirmed ? 'badge-brand' : 'badge-warn'}` },
        p.confirmed ? 'bestätigt' : 'provisorisch',
      ),
      p.barcode ? el('span', { class: 'tiny tabular' }, `Barcode ${p.barcode}`) : null,
      p.lastPrice != null
        ? el('span', { class: 'tiny tabular' }, `zuletzt ${formatEuro(p.lastPrice)}`)
        : null,
    ]),

    el('div', { class: 'field' }, [el('label', {}, 'Name'), name]),
    el('div', { class: 'field' }, [el('label', {}, 'Marke'), brand]),
    el('div', { class: 'grid-2' }, [
      el('div', { class: 'field' }, [el('label', {}, 'Kategorie'), category]),
      el('div', { class: 'field' }, [el('label', {}, 'Lagerort'), location]),
    ]),
    el('div', { class: 'field' }, [
      el('label', {}, 'Packungsgröße'),
      el('div', { class: 'input-row' }, [sizeValue, sizeUnit]),
    ]),

    el('div', { class: 'field' }, [
      el('label', {}, 'Meldebestand (min. ganze Packungen)'),
      minStock,
    ]),
    el('div', { class: 'switch-row' }, [
      el('span', { class: 'switch-label' }, 'Kernartikel'),
      coreSwitch,
    ]),
    el(
      'p',
      { class: 'field-hint' },
      'Kernartikel sollen nie ausgehen: Fällt der Vorrat unter den Meldebestand, landet der Artikel automatisch auf der Einkaufsliste.',
    ),

    sectionTitle('Chargen & Haltbarkeit'),
    batchWrap,
    el(
      'button',
      {
        class: 'btn btn-secondary btn-block',
        type: 'button',
        onclick: () => {
          sheet.close();
          openAddStockSheet(p);
        },
      },
      [icon('plus', { size: 18 }), 'Bestand mit MHD zubuchen'],
    ),

    sectionTitle('Erkannte Bezeichnungen'),
    aliasWrap,
  ]);

  const sheet = openSheet(body, { title: 'Produkt bearbeiten' });
  void loadBatches(p.id, batchWrap);
  void loadAliases(p.id, aliasWrap);

  sheet.addActions(
    el(
      'button',
      {
        class: 'btn btn-primary btn-block',
        type: 'button',
        onclick: guarded(
          async () => {
            const parsed =
              sizeValue.value && sizeUnit.value
                ? toBaseSize(sizeValue.value, sizeUnit.value)
                : { value: null, unit: null };
            const { product } = await api<{ product: ProductView }>(`/api/products/${p.id}`, {
              method: 'PATCH',
              body: {
                name: name.value.trim() || p.name,
                brand: brand.value.trim() || null,
                category: category.value || null,
                storageLocation: location.value || null,
                packageSizeValue: parsed.value,
                packageSizeUnit: parsed.unit,
                defaultUnit: parsed.unit,
                minStock: minStock.value === '' ? null : Number(minStock.value),
                isCore: coreSwitch.getAttribute('aria-checked') === 'true',
              },
            });
            upsertProduct(product);
            sheet.close();
          },
          { success: 'Gespeichert', failure: 'Speichern fehlgeschlagen' },
        ),
      },
      'Speichern',
    ),
    el(
      'button',
      {
        class: 'btn btn-secondary btn-block',
        type: 'button',
        onclick: guarded(
          async () => {
            await api('/api/shopping', { method: 'POST', body: { productId: p.id } });
            sheet.close();
          },
          { success: 'Auf die Einkaufsliste gesetzt' },
        ),
      },
      [icon('cart', { size: 18 }), 'Auf die Einkaufsliste'],
    ),
    el(
      'button',
      {
        class: 'btn btn-danger btn-block',
        type: 'button',
        onclick: async () => {
          const ok = await confirmDialog({
            title: `„${p.name}" löschen?`,
            message:
              'Bestand und Chargen verschwinden mit. Einträge auf der Einkaufsliste bleiben als Freitext erhalten.',
            confirmLabel: 'Löschen',
            danger: true,
          });
          if (!ok) return;
          await guard(
            async () => {
              await api(`/api/products/${p.id}`, { method: 'DELETE' });
              removeProduct(p.id);
              sheet.close();
            },
            { success: 'Gelöscht', failure: 'Löschen fehlgeschlagen' },
          );
        },
      },
      'Löschen',
    ),
  );
}

const CATEGORY_OPTIONS = [
  'obst_gemuese',
  'backwaren',
  'milchprodukte',
  'kaese',
  'fleisch_wurst',
  'fisch',
  'tiefkuehl',
  'teigwaren_reis',
  'konserven',
  'gewuerze_backen',
  'snacks_suesses',
  'getraenke',
  'haushalt',
  'drogerie',
  'sonstiges',
].map((key) => findCategory(key));

async function loadBatches(productId: number, wrap: HTMLElement): Promise<void> {
  try {
    const { batches } = await api<{ batches: BatchView[] }>(`/api/products/${productId}/batches`);
    if (!batches.length) {
      replace(wrap, el('p', { class: 'tiny' }, 'Noch keine Chargen mit Datum erfasst.'));
      return;
    }
    replace(
      wrap,
      batches.map((b) =>
        el('div', { class: 'batch-row' }, [
          el('span', { class: 'tabular' }, `${b.qty}×`),
          el('span', {}, b.bestBefore ? `MHD ${b.bestBefore}` : 'ohne Datum'),
          expiryBadge(b.daysLeft),
          el(
            'button',
            {
              class: 'btn btn-sm btn-ghost',
              type: 'button',
              onclick: guarded(
                async () => {
                  await api(`/api/batches/${b.id}/resolve`, {
                    method: 'POST',
                    body: { outcome: 'consumed' },
                  });
                  await Promise.all([refreshProducts(), refreshExpiring()]);
                  void loadBatches(productId, wrap);
                },
                { success: 'Abgebucht' },
              ),
            },
            'aufgebraucht',
          ),
        ]),
      ),
    );
  } catch {
    replace(wrap, el('p', { class: 'tiny' }, 'Chargen konnten nicht geladen werden.'));
  }
}

async function loadAliases(productId: number, wrap: HTMLElement): Promise<void> {
  try {
    const { aliases } = await api<{
      aliases: { id: number; text: string; source: string }[];
    }>(`/api/products/${productId}/aliases`);

    if (!aliases.length) {
      replace(
        wrap,
        el('p', { class: 'tiny' }, 'Noch keine – Bon-Kürzel lernt die App beim Buchen.'),
      );
      return;
    }

    replace(
      wrap,
      aliases.map((a) =>
        el('span', { class: 'chip' }, [
          el('span', {}, a.text),
          el(
            'button',
            {
              class: 'chip-remove',
              type: 'button',
              'aria-label': `Zuordnung „${a.text}" entfernen`,
              onclick: guarded(
                async (e) => {
                  await api(`/api/product-aliases/${a.id}`, { method: 'DELETE' });
                  (e.currentTarget as HTMLElement).closest('.chip')?.remove();
                },
                { success: 'Zuordnung entfernt' },
              ),
            },
            icon('x', { size: 14 }),
          ),
        ]),
      ),
    );
  } catch {
    replace(wrap, el('p', { class: 'tiny' }, '—'));
  }
}

export async function refreshProducts(): Promise<void> {
  const { products } = await api<{ products: ProductView[] }>('/api/products');
  applyProducts(products);
}

export { toast };
